// src/pages/TeacherReport.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { ref, get } from 'firebase/database';

const TeacherReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [dateRangeType, setDateRangeType] = useState('month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [allTimetables, setAllTimetables] = useState({});
  const [arrangementHistory, setArrangementHistory] = useState([]);
  const [debugInfo, setDebugInfo] = useState('');
  const [undatedSubstitutions, setUndatedSubstitutions] = useState([]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const periods = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'];

  // Helper function to normalize text
  const normalizeText = (value) => {
    return String(value || '').trim().toLowerCase();
  };

  // Check admin authentication
  useEffect(() => {
    const isAdminLoggedIn = sessionStorage.getItem('isAdminLoggedIn');
    if (!isAdminLoggedIn) {
      navigate('/login');
    }
  }, [navigate]);

  // Load teachers and data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teachersSnap, timetablesSnap, arrangementsSnap] = await Promise.all([
          get(ref(db, 'teachers')),
          get(ref(db, 'timetables')),
          get(ref(db, 'arrangementHistory'))
        ]);

        if (teachersSnap.exists()) {
          const teachersData = teachersSnap.val();
          const activeTeachers = Object.values(teachersData).filter(t => t.status !== 'inactive');
          setTeachers(activeTeachers);
        }

        if (timetablesSnap.exists()) {
          setAllTimetables(timetablesSnap.val());
        }

        if (arrangementsSnap.exists()) {
          const rawData = arrangementsSnap.val();
          console.log('📋 RAW ARRANGEMENT HISTORY:', rawData);
          
          // Flatten the arrangement history - handle nested structure
          const flattened = flattenArrangements(rawData);
          console.log('📋 FLATTENED ARRANGEMENTS:', flattened);
          setArrangementHistory(flattened);
        }

        // Set default date range
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setFromDate(formatDateInput(firstDay));
        setToDate(formatDateInput(lastDay));
      } catch (error) {
        console.error('Error fetching data:', error);
        setDebugInfo('Error loading data: ' + error.message);
      }
    };

    fetchData();
  }, []);

  // Flatten arrangement history - handles nested structures
  const flattenArrangements = (data) => {
    const result = [];

    if (!data || typeof data !== 'object') {
      return result;
    }

    // Recursive function to flatten nested objects
    const flatten = (obj, parentKey = null) => {
      if (!obj || typeof obj !== 'object') return;

      // Check if this looks like an arrangement record
      const isArrangement = (item) => {
        return item && typeof item === 'object' && (
          item.substituteTeacher ||
          item.class ||
          item.period ||
          item.subject ||
          item.day
        );
      };

      // If this object has arrangement-like properties, it's a record
      if (isArrangement(obj)) {
        // Try to extract date from parent key if it looks like a date
        let dateValue = obj.date || obj.arrangementDate || obj.savedAt || obj.createdAt || obj.timestamp || null;
        
        // If parent key looks like a date (YYYY-MM-DD), use it
        if (!dateValue && parentKey && /^\d{4}-\d{2}-\d{2}$/.test(parentKey)) {
          dateValue = parentKey;
        }

        // Also check if there's a generatedAt field
        const generatedAt = obj.generatedAt || null;

        result.push({
          ...obj,
          _date: dateValue,
          _generatedAt: generatedAt,
          _parentKey: parentKey
        });
        return;
      }

      // If not an arrangement, recurse into children
      Object.entries(obj).forEach(([key, value]) => {
        if (value && typeof value === 'object') {
          flatten(value, key);
        }
      });
    };

    flatten(data);
    return result;
  };

  const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDayName = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  const setDateRange = (type) => {
    const today = new Date();
    let start, end;

    switch(type) {
      case 'today':
        start = end = today;
        break;
      case 'week':
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start = new Date(today);
        start.setDate(diff);
        end = new Date(today);
        end.setDate(diff + 6);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      default:
        return;
    }

    setFromDate(formatDateInput(start));
    setToDate(formatDateInput(end));
    setDateRangeType(type);
  };

  const generateReport = () => {
    if (!selectedTeacher || !fromDate || !toDate) {
      alert('Please select a teacher and date range');
      return;
    }

    setLoading(true);
    setDebugInfo('');
    setUndatedSubstitutions([]);

    try {
      const start = new Date(fromDate + 'T00:00:00');
      const end = new Date(toDate + 'T23:59:59.999');

      console.log(`📊 Generating report for teacher: ${selectedTeacher}`);
      console.log(`📅 Date range: ${start} to ${end}`);

      // Calculate regular classes
      let regularPeriods = 0;
      const subjects = new Set();
      const classesSet = new Set();
      const dailyRegular = {};

      Object.entries(allTimetables).forEach(([className, classData]) => {
        if (!classData || typeof classData !== 'object') return;

        Object.entries(classData).forEach(([day, dayData]) => {
          if (!dayData || typeof dayData !== 'object') return;

          Object.entries(dayData).forEach(([period, periodData]) => {
            if (!periodData || typeof periodData !== 'object') return;

            const teacherName = periodData.teacher;
            if (normalizeText(teacherName) === normalizeText(selectedTeacher)) {
              regularPeriods++;
              subjects.add(periodData.subject || 'Unknown');
              classesSet.add(className);
              
              if (!dailyRegular[day]) dailyRegular[day] = 0;
              dailyRegular[day]++;
            }
          });
        });
      });

      // Calculate substitutions from arrangementHistory
      const validSubstitutions = [];
      const legacySubstitutions = [];
      let substitutionCount = 0;

      arrangementHistory.forEach(arrangement => {
        if (!arrangement || typeof arrangement !== 'object') return;

        // Check if this arrangement belongs to the selected teacher
        const substituteTeacher = arrangement.substituteTeacher || arrangement.substitute_teacher || arrangement.teacher;
        const isMatchingTeacher = normalizeText(substituteTeacher) === normalizeText(selectedTeacher);
        
        if (!isMatchingTeacher) return;

        // Try to extract date from various fields
        let dateValue = arrangement._date || arrangement.date || arrangement.arrangementDate || 
                       arrangement.savedAt || arrangement.createdAt || arrangement.timestamp;

        // Check if there's a generatedAt field
        const generatedAt = arrangement.generatedAt || arrangement._generatedAt;

        // Build the arrangement record
        const record = {
          date: dateValue,
          day: arrangement.day || arrangement.Day || getDayName(new Date(dateValue)) || 'Unknown',
          period: arrangement.period || arrangement.Period || 'N/A',
          class: arrangement.class || arrangement.Class || arrangement.className || 'N/A',
          subject: arrangement.subject || arrangement.Subject || 'N/A',
          absentTeacher: arrangement.absentTeacher || arrangement.absent_teacher || arrangement.teacher || 'N/A',
          reason: arrangement.substituteReason || arrangement.reason || arrangement.substitute_reason || 'N/A',
          generatedAt: generatedAt,
          raw: arrangement
        };

        // If we have a valid date, check if it's in range
        if (dateValue) {
          try {
            const arrDate = new Date(dateValue);
            if (!isNaN(arrDate.getTime())) {
              // Valid date - check range
              if (arrDate >= start && arrDate <= end) {
                validSubstitutions.push(record);
                substitutionCount++;
              }
              return;
            }
          } catch (e) {
            console.log(`Invalid date value: ${dateValue}`);
          }
        }

        // If we reach here, the date is invalid or missing
        // Check if it has a generatedAt that might indicate a date
        if (generatedAt) {
          // Try to parse generatedAt (might be like "12:26:13 PM" - not a date)
          // Skip - it's just time
        }

        // This is a legacy/undated record
        legacySubstitutions.push({
          ...record,
          date: 'Date Not Saved'
        });
        setUndatedSubstitutions(prev => [...prev, record]);
      });

      // Sort valid substitutions by date (newest first)
      validSubstitutions.sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date) - new Date(a.date);
      });

      console.log(`✅ Found ${validSubstitutions.length} valid substitutions`);
      console.log(`📋 Found ${legacySubstitutions.length} legacy/undated substitutions`);

      setReportData({
        teacher: selectedTeacher,
        period: `${formatDateDisplay(start)} to ${formatDateDisplay(end)}`,
        startDate: start,
        endDate: end,
        regularPeriods,
        subjects: Array.from(subjects),
        classes: Array.from(classesSet),
        dailyRegular,
        substitutionCount,
        substitutions: validSubstitutions,
        legacyCount: legacySubstitutions.length,
        legacySubstitutions: legacySubstitutions
      });

      setDebugInfo(`Report generated successfully. Found ${regularPeriods} regular classes and ${substitutionCount} substitution classes.`);
    } catch (error) {
      console.error('Error generating report:', error);
      setDebugInfo('Error generating report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem('isAdminLoggedIn');
    navigate('/login');
  };

  // Go back to dashboard
  const handleBack = () => {
    navigate('/admin');
  };

  // Print functionality
  const handlePrint = () => {
    const printContent = document.getElementById('report-print');
    if (!printContent) return;

    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.outerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // WhatsApp functionality
  const getWhatsAppMessage = () => {
    if (!reportData) return '';

    let message = 'MIS INTERNATIONAL SCHOOL\n';
    message += 'TEACHER WORKLOAD & SUBSTITUTION REPORT\n\n';
    message += `Teacher: ${reportData.teacher}\n`;
    message += `Period: ${reportData.period}\n\n`;
    message += '='.repeat(30) + '\n\n';
    message += 'REGULAR WORKLOAD\n';
    message += `Weekly Classes: ${reportData.regularPeriods}\n`;
    message += `Subjects: ${reportData.subjects.join(', ')}\n\n`;
    message += 'SUBSTITUTION SUMMARY\n';
    message += `Total Substitution Classes: ${reportData.substitutionCount}\n`;
    
    if (reportData.legacyCount > 0) {
      message += `Legacy/Undated Substitutions: ${reportData.legacyCount}\n`;
    }
    message += '\n';

    if (reportData.substitutions.length > 0) {
      message += 'SUBSTITUTION DETAILS\n';
      message += '-'.repeat(20) + '\n';
      reportData.substitutions.forEach(sub => {
        const dateDisplay = sub.date ? formatDateDisplay(new Date(sub.date)) : 'Date Not Saved';
        message += `${dateDisplay}\n`;
        message += `${sub.day} - ${sub.period} - ${sub.class}\n`;
        message += `Subject: ${sub.subject}\n`;
        message += `For Absent Teacher: ${sub.absentTeacher}\n`;
        message += `Reason: ${sub.reason}\n\n`;
      });
    } else {
      message += 'No substitutions found for this period.\n';
    }

    if (reportData.legacySubstitutions && reportData.legacySubstitutions.length > 0) {
      message += '\n' + '='.repeat(30) + '\n\n';
      message += 'LEGACY / UNDATED SUBSTITUTIONS\n';
      message += '-'.repeat(20) + '\n';
      reportData.legacySubstitutions.forEach(sub => {
        message += `Date: Date Not Saved\n`;
        message += `${sub.day} - ${sub.period} - ${sub.class}\n`;
        message += `Subject: ${sub.subject}\n`;
        message += `For Absent Teacher: ${sub.absentTeacher}\n\n`;
      });
    }

    message += '='.repeat(30) + '\n';
    message += 'Generated by School Timetable System\n';
    message += 'Powered by Jauhari WebTech';

    return message;
  };

  const handleWhatsApp = (isBusiness = false) => {
    const message = getWhatsAppMessage();
    if (!message) return;

    const encodedMessage = encodeURIComponent(message);
    const url = isBusiness
      ? `https://api.whatsapp.com/send?phone=&text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    window.open(url, '_blank');
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={handleBack} style={styles.backBtn}>← Back</button>
          <h1 style={styles.headerTitle}>📄 Teacher Report</h1>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          🚪 Logout
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {/* Controls */}
        <div style={styles.controls}>
          <div style={styles.selectorGroup}>
            <label style={styles.label}>Select Teacher</label>
            <select
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              style={styles.select}
            >
              <option value="">-- Select a Teacher --</option>
              {teachers.map((teacher, index) => (
                <option key={index} value={teacher.name}>
                  {teacher.name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.dateQuickSelect}>
            <button 
              onClick={() => setDateRange('today')} 
              style={{...styles.dateBtn, ...(dateRangeType === 'today' ? styles.dateBtnActive : {})}}
            >
              Today
            </button>
            <button 
              onClick={() => setDateRange('week')} 
              style={{...styles.dateBtn, ...(dateRangeType === 'week' ? styles.dateBtnActive : {})}}
            >
              This Week
            </button>
            <button 
              onClick={() => setDateRange('month')} 
              style={{...styles.dateBtn, ...(dateRangeType === 'month' ? styles.dateBtnActive : {})}}
            >
              This Month
            </button>
            <button 
              onClick={() => setDateRangeType('custom')} 
              style={{...styles.dateBtn, ...(dateRangeType === 'custom' ? styles.dateBtnActive : {})}}
            >
              Custom
            </button>
          </div>

          <div style={styles.dateInputs}>
            <div style={styles.dateGroup}>
              <label style={styles.dateLabel}>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setDateRangeType('custom');
                }}
                style={styles.dateInput}
              />
            </div>
            <div style={styles.dateGroup}>
              <label style={styles.dateLabel}>To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setDateRangeType('custom');
                }}
                style={styles.dateInput}
              />
            </div>
          </div>

          <button onClick={generateReport} style={styles.generateBtn}>
            📊 Generate Report
          </button>
        </div>

        {debugInfo && (
          <div style={styles.debugInfo}>
            ℹ️ {debugInfo}
          </div>
        )}

        {loading && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Generating report...</p>
          </div>
        )}

        {/* Report Display */}
        {reportData && !loading && (
          <>
            <div style={styles.reportActions}>
              <button onClick={handlePrint} style={styles.btnPrint}>
                🖨 Print Report
              </button>
              <button onClick={() => handleWhatsApp(false)} style={styles.btnWhatsapp}>
                📱 WhatsApp
              </button>
              <button onClick={() => handleWhatsApp(true)} style={styles.btnWhatsappBusiness}>
                💼 WhatsApp Business
              </button>
            </div>

            <div id="report-print" style={styles.reportDisplay}>
              <div style={styles.reportHeader}>
                <h2 style={styles.reportSchoolName}>MIS INTERNATIONAL SCHOOL</h2>
                <h3 style={styles.reportTitle}>TEACHER WORKLOAD &amp; SUBSTITUTION REPORT</h3>
                <p style={styles.reportTeacher}>Teacher: {reportData.teacher}</p>
                <p style={styles.reportPeriod}>Period: {reportData.period}</p>
              </div>

              <div style={styles.reportSummary}>
                <div style={styles.summaryCard}>
                  <span style={styles.summaryNumber}>{reportData.regularPeriods}</span>
                  <span style={styles.summaryLabel}>Regular Weekly Classes</span>
                </div>
                <div style={styles.summaryCard}>
                  <span style={styles.summaryNumber}>{reportData.substitutionCount}</span>
                  <span style={styles.summaryLabel}>Substitution Classes</span>
                </div>
                <div style={styles.summaryCard}>
                  <span style={styles.summaryNumber}>{reportData.classes.length}</span>
                  <span style={styles.summaryLabel}>Classes/Sections Taught</span>
                </div>
                <div style={styles.summaryCard}>
                  <span style={styles.summaryNumber}>{reportData.subjects.length}</span>
                  <span style={styles.summaryLabel}>Subjects Taught</span>
                </div>
                {reportData.legacyCount > 0 && (
                  <div style={{...styles.summaryCard, borderLeftColor: '#f59e0b'}}>
                    <span style={styles.summaryNumber}>{reportData.legacyCount}</span>
                    <span style={styles.summaryLabel}>Legacy/Undated Substitutions</span>
                  </div>
                )}
              </div>

              <div style={styles.reportDetails}>
                <div style={styles.reportSection}>
                  <h3 style={styles.reportSectionTitle}>📚 REGULAR WORKLOAD</h3>
                  <p style={styles.reportText}>
                    <strong>Weekly Classes:</strong> {reportData.regularPeriods}
                  </p>
                  <p style={styles.reportText}>
                    <strong>Subjects:</strong> {reportData.subjects.join(', ')}
                  </p>
                </div>

                <div style={styles.reportSection}>
                  <h3 style={styles.reportSectionTitle}>📋 SUBSTITUTION SUMMARY</h3>
                  <p style={styles.reportText}>
                    <strong>Total Substitution Classes:</strong> {reportData.substitutionCount}
                  </p>
                  {reportData.legacyCount > 0 && (
                    <p style={styles.reportText}>
                      <strong>Legacy/Undated Substitutions:</strong> {reportData.legacyCount}
                    </p>
                  )}
                </div>

                {reportData.substitutions.length > 0 && (
                  <div style={styles.reportSection}>
                    <h3 style={styles.reportSectionTitle}>📝 SUBSTITUTION DETAILS</h3>
                    <div style={styles.substitutionTable}>
                      <div style={styles.tableHeader}>
                        <span style={styles.tableHeaderCell}>Date</span>
                        <span style={styles.tableHeaderCell}>Day</span>
                        <span style={styles.tableHeaderCell}>Period</span>
                        <span style={styles.tableHeaderCell}>Class</span>
                        <span style={styles.tableHeaderCell}>Subject</span>
                        <span style={styles.tableHeaderCell}>Absent Teacher</span>
                        <span style={styles.tableHeaderCell}>Reason</span>
                      </div>
                      {reportData.substitutions.map((sub, index) => {
                        const dateDisplay = sub.date ? formatDateDisplay(new Date(sub.date)) : 'Date Not Saved';
                        return (
                          <div key={index} style={styles.tableRow}>
                            <span style={styles.tableCell}>{dateDisplay}</span>
                            <span style={styles.tableCell}>{sub.day}</span>
                            <span style={styles.tableCell}>{sub.period}</span>
                            <span style={styles.tableCell}>{sub.class}</span>
                            <span style={styles.tableCell}>{sub.subject}</span>
                            <span style={styles.tableCell}>{sub.absentTeacher}</span>
                            <span style={styles.tableCell}>{sub.reason}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {reportData.legacySubstitutions && reportData.legacySubstitutions.length > 0 && (
                  <div style={styles.reportSection}>
                    <h3 style={{...styles.reportSectionTitle, color: '#f59e0b'}}>📜 LEGACY / UNDATED SUBSTITUTIONS</h3>
                    <div style={styles.substitutionTable}>
                      <div style={styles.tableHeader}>
                        <span style={styles.tableHeaderCell}>Date</span>
                        <span style={styles.tableHeaderCell}>Day</span>
                        <span style={styles.tableHeaderCell}>Period</span>
                        <span style={styles.tableHeaderCell}>Class</span>
                        <span style={styles.tableHeaderCell}>Subject</span>
                        <span style={styles.tableHeaderCell}>Absent Teacher</span>
                        <span style={styles.tableHeaderCell}>Reason</span>
                      </div>
                      {reportData.legacySubstitutions.map((sub, index) => (
                        <div key={index} style={{...styles.tableRow, background: '#fff8f0'}}>
                          <span style={styles.tableCell}>Date Not Saved</span>
                          <span style={styles.tableCell}>{sub.day}</span>
                          <span style={styles.tableCell}>{sub.period}</span>
                          <span style={styles.tableCell}>{sub.class}</span>
                          <span style={styles.tableCell}>{sub.subject}</span>
                          <span style={styles.tableCell}>{sub.absentTeacher}</span>
                          <span style={styles.tableCell}>{sub.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.reportFooter}>
                <p>Generated by School Timetable &amp; Teacher Arrangement Management System</p>
                <p>Powered by Jauhari WebTech</p>
                <p>© 2026 Jauhari WebTech. All Rights Reserved.</p>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>Powered by Jauhari WebTech</p>
        <p>© 2026 Jauhari WebTech. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f7fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
  },

  // Header
  header: {
    background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 100%)',
    color: 'white',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  headerTitle: {
    fontSize: '18px',
    margin: 0,
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '8px 18px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },

  // Main Content
  mainContent: {
    flex: 1,
    padding: '15px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },

  // Controls
  controls: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  selectorGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '15px',
  },
  label: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '14px',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    background: 'white',
    WebkitAppearance: 'none',
    appearance: 'none',
  },
  dateQuickSelect: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '15px',
  },
  dateBtn: {
    flex: 1,
    minWidth: '60px',
    padding: '8px 12px',
    border: '2px solid #e0e0e0',
    background: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    transition: 'all 0.3s ease',
  },
  dateBtnActive: {
    borderColor: '#2d1b4e',
    background: '#2d1b4e',
    color: 'white',
  },
  dateInputs: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '15px',
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dateLabel: {
    fontSize: '12px',
    color: '#7f8c8d',
  },
  dateInput: {
    padding: '10px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
  },
  generateBtn: {
    width: '100%',
    padding: '12px',
    background: '#2d1b4e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

  // Debug Info
  debugInfo: {
    padding: '10px 14px',
    background: '#f8f9fa',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#666',
    marginBottom: '15px',
    borderLeft: '3px solid #2d1b4e',
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTop: '4px solid #2d1b4e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  // Report Actions
  reportActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
  btnPrint: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#2c3e50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  btnWhatsapp: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#25D366',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  btnWhatsappBusiness: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 15px',
    background: '#075E54',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

  // Report Display
  reportDisplay: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  reportHeader: {
    textAlign: 'center',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
    marginBottom: '15px',
  },
  reportSchoolName: {
    margin: 0,
    fontSize: '20px',
    color: '#2c3e50',
  },
  reportTitle: {
    margin: '4px 0',
    fontSize: '16px',
    color: '#7f8c8d',
  },
  reportTeacher: {
    margin: '4px 0 0 0',
    fontSize: '15px',
    fontWeight: 600,
    color: '#2c3e50',
  },
  reportPeriod: {
    margin: '2px 0 0 0',
    fontSize: '13px',
    color: '#95a5a6',
  },

  // Report Summary
  reportSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '10px',
    marginBottom: '20px',
  },
  summaryCard: {
    background: '#f8f9fa',
    padding: '12px',
    borderRadius: '8px',
    textAlign: 'center',
    borderLeft: '3px solid #2d1b4e',
  },
  summaryNumber: {
    display: 'block',
    fontSize: '24px',
    fontWeight: 700,
    color: '#2c3e50',
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#7f8c8d',
  },

  // Report Details
  reportDetails: {
    marginTop: '10px',
  },
  reportSection: {
    marginBottom: '15px',
  },
  reportSectionTitle: {
    color: '#2c3e50',
    fontSize: '15px',
    margin: '0 0 8px 0',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '4px',
  },
  reportText: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#2c3e50',
  },

  // Substitution Table
  substitutionTable: {
    overflowX: 'auto',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr',
    background: '#2d1b4e',
    color: 'white',
    padding: '8px',
    fontWeight: 600,
    fontSize: '10px',
    borderRadius: '4px 4px 0 0',
    gap: '4px',
  },
  tableHeaderCell: {
    textAlign: 'center',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr',
    padding: '6px',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '10px',
    gap: '4px',
  },
  tableCell: {
    textAlign: 'center',
  },

  // Report Footer
  reportFooter: {
    textAlign: 'center',
    paddingTop: '15px',
    borderTop: '2px solid #f0f0f0',
    marginTop: '15px',
  },

  // Footer
  footer: {
    background: 'white',
    textAlign: 'center',
    padding: '12px',
    borderTop: '1px solid #e0e0e0',
    marginTop: '20px',
  },
};

// Add keyframe animations and print styles
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media print {
    body * {
      visibility: hidden;
    }
    #report-print, #report-print * {
      visibility: visible;
    }
    #report-print {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 20px !important;
    }
    .table-header {
      background: #2d1b4e !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .summary-card {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .report-school-name {
      font-size: 16px !important;
    }
    .report-title {
      font-size: 12px !important;
    }
    .table-row {
      font-size: 8px !important;
      padding: 3px !important;
    }
    .table-header {
      font-size: 8px !important;
      padding: 3px !important;
    }
    .table-cell {
      font-size: 8px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default TeacherReport;